/**
 * Utility functions for color manipulation and contrast calculations
 */

/**
 * Converts a hex color to RGB values
 * @param hex - Hex color string (e.g., '#FF5733' or 'FF5733')
 * @returns Object with r, g, b values (0-255)
 */
export const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  // Remove # if present
  const cleanHex = hex.replace('#', '');

  // Handle 3-digit hex
  if (cleanHex.length === 3) {
    const r = parseInt(cleanHex[0] + cleanHex[0], 16);
    const g = parseInt(cleanHex[1] + cleanHex[1], 16);
    const b = parseInt(cleanHex[2] + cleanHex[2], 16);
    return { r, g, b };
  }

  // Handle 6-digit hex
  if (cleanHex.length === 6) {
    const r = parseInt(cleanHex.substring(0, 2), 16);
    const g = parseInt(cleanHex.substring(2, 4), 16);
    const b = parseInt(cleanHex.substring(4, 6), 16);
    return { r, g, b };
  }

  return null;
};

/**
 * Calculates the relative luminance of a color
 * Based on WCAG 2.0 formula: https://www.w3.org/TR/WCAG20/#relativeluminancedef
 * @param r - Red value (0-255)
 * @param g - Green value (0-255)
 * @param b - Blue value (0-255)
 * @returns Relative luminance (0-1)
 */
export const getRelativeLuminance = (r: number, g: number, b: number): number => {
  // Convert to 0-1 range
  const rsRGB = r / 255;
  const gsRGB = g / 255;
  const bsRGB = b / 255;

  // Apply gamma correction
  const rLinear = rsRGB <= 0.03928 ? rsRGB / 12.92 : Math.pow((rsRGB + 0.055) / 1.055, 2.4);
  const gLinear = gsRGB <= 0.03928 ? gsRGB / 12.92 : Math.pow((gsRGB + 0.055) / 1.055, 2.4);
  const bLinear = bsRGB <= 0.03928 ? bsRGB / 12.92 : Math.pow((bsRGB + 0.055) / 1.055, 2.4);

  // Calculate relative luminance
  return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
};

/**
 * Calculates the contrast ratio between two colors
 * Based on WCAG 2.0 formula: https://www.w3.org/TR/WCAG20/#contrast-ratiodef
 * @param l1 - Luminance of first color
 * @param l2 - Luminance of second color
 * @returns Contrast ratio (1-21)
 */
export const getContrastRatio = (l1: number, l2: number): number => {
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
};

/**
 * Determines if text should be white or black for optimal contrast on a given background color
 * Uses WCAG 2.0 AA standard (4.5:1 for normal text)
 * @param backgroundColor - Hex color string (e.g., '#FF5733')
 * @returns 'white' or 'black'
 */
export const getContrastTextColor = (backgroundColor: string): 'white' | 'black' => {
  const rgb = hexToRgb(backgroundColor);

  if (!rgb) {
    // Default to black if color parsing fails
    return 'black';
  }

  const bgLuminance = getRelativeLuminance(rgb.r, rgb.g, rgb.b);

  // Luminance of white (1) and black (0)
  const whiteLuminance = 1;
  const blackLuminance = 0;

  const whiteContrast = getContrastRatio(bgLuminance, whiteLuminance);
  const blackContrast = getContrastRatio(bgLuminance, blackLuminance);

  // Return the color with better contrast
  return whiteContrast > blackContrast ? 'white' : 'black';
};

/**
 * Validates if a hex color string is valid
 * @param hex - Hex color string
 * @returns true if valid, false otherwise
 */
export const isValidHexColor = (hex: string): boolean => {
  const cleanHex = hex.replace('#', '');
  return /^[0-9A-Fa-f]{3}$|^[0-9A-Fa-f]{6}$/.test(cleanHex);
};

/**
 * Ensures a hex color has a # prefix
 * @param hex - Hex color string
 * @returns Hex color with # prefix
 */
export const normalizeHexColor = (hex: string): string => {
  const cleanHex = hex.replace('#', '');
  return `#${cleanHex}`;
};
